// Renderless poller for global processing jobs. Mounted once in
// DashboardLayout so batches keep being tracked across route changes, hidden
// tabs (refetchIntervalInBackground), and — via the sessionStorage-persisted
// store — full page reloads.
//
// One JobPoller per active job: each owns its own useQuery on the shared
// ['batchStatus', projectId, batchId] key (dedupes with any page-level
// poller observing the same batch) and reduces the status into store patches.
// On the completion rising edge it fans out cache invalidation so the
// programs list / command center / pre-award workspace populate without a
// manual refresh, then resolves the destination link for the toast + tray.
//
// Lifecycle after completion:
//   contract-doc — patched to ready; toast shows the View link. Auto-removal
//     from the store is owned by ProcessingTray's TrayJob component via a
//     useEffect so the timer can be cancelled if the user clicks View/Dismiss.
//   award-intake — NOT auto-removed; the pre-award launch panel owns its
//     lifetime (it needs the awardDocId until Begin Award Comparison fires).
import { jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
import { Code, ConnectError } from '@connectrpc/connect';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/authContext';
import { proposalProjectApiConnect } from '../../lib/api/proposalProjectApi.connect';
import { deliveryClient } from '../../lib/connect/clients';
import { isRehydratedJob, selectProcessingJobs, useProcessingJobsStore } from '../../lib/processing/jobsStore';
import { blendedPercent } from '../../lib/processing/progress';
import { notAContractCopy } from './ProcessingTray';
const POLL_INTERVAL_MS = 2000;
// Same ceiling as the old AwardIntakePanel cap (450 polls × 2s): covers both
// the per-doc extraction and the project-analysis phase that follows it.
const MAX_PROCESSING_MS = 15 * 60 * 1000;
// The program link resolves via ryugu after reverie's sync + scope seed land;
// give the projection a few beats to catch up before falling back to the
// project workspace link — this is the golden-path "View" moment.
const LINK_RESOLVE_ATTEMPTS = 3;
const LINK_RESOLVE_DELAY_MS = 1500;
export function ProcessingJobsHost() {
    const jobs = useProcessingJobsStore(selectProcessingJobs);
    return /*#__PURE__*/ _jsx(_Fragment, {
        children: jobs.map((job)=>/*#__PURE__*/ _jsx(JobPoller, {
                job: job
            }, job.batchId))
    });
}
function isNotFound(err) {
    return err instanceof ConnectError && err.code === Code.NotFound;
}
const sleep = (ms)=>new Promise((resolve)=>setTimeout(resolve, ms));
/**
 * Resolve the delivery-program link for a finished contract batch, retrying
 * briefly: the program is created by reverie's sync and made visible by the
 * viewer-projection seed, both of which can land seconds after the batch
 * reports complete. Returns null when the program isn't visible yet.
 */ async function resolveProgramLink(tenantId, zouProjectId, syncedProgramId) {
    if (syncedProgramId) {
        return `/delivery/programs/${syncedProgramId}`;
    }
    for(let attempt = 0; attempt < LINK_RESOLVE_ATTEMPTS; attempt++){
        if (attempt > 0) await sleep(LINK_RESOLVE_DELAY_MS);
        try {
            const resp = await deliveryClient.listPrograms({
                companyId: tenantId,
                search: '',
                sectorId: '',
                businessUnitId: '',
                portfolioId: '',
                operationalStateId: '',
                zouProjectId
            });
            const program = resp.programs[0];
            if (program) return `/delivery/programs/${program.id}`;
        } catch  {
        // Transient read failure — the next attempt (or the fallback link)
        // covers it; completion itself is never blocked on link resolution.
        }
    }
    return null;
}
function JobPoller({ job }) {
    const queryClient = useQueryClient();
    const navigate = useNavigate();
    const { user } = useAuth();
    const patchJob = useProcessingJobsStore((s)=>s.patchJob);
    const removeJob = useProcessingJobsStore((s)=>s.removeJob);
    const { data, error } = useQuery({
        queryKey: [
            'batchStatus',
            job.zouProjectId,
            job.batchId
        ],
        queryFn: ()=>proposalProjectApiConnect.getBatchStatus(job.zouProjectId, job.batchId),
        // Stop once the job has left 'processing' (patched to ready/failed below)
        // or the query itself has errored — otherwise a NotFound keeps re-firing
        // every 2s forever even after it already patched the job to failed.
        refetchInterval: (query)=>job.status === 'processing' && !query.state.error ? POLL_INTERVAL_MS : false,
        // The whole point of the host: a hidden tab must not stall tracking.
        refetchIntervalInBackground: true,
        staleTime: 1500,
        retry: (failureCount, err)=>isNotFound(err) ? false : failureCount < 3
    });
    // tenant_id is optional on User and the host mounts above delivery routes,
    // so read it defensively instead of useTenantId() (which throws).
    const tenantId = user?.tenant_id ?? null;
    const completing = useRef(false);
    // Whether THIS poller has ever seen a real response for this job. A job
    // rehydrated from sessionStorage (present before this page load) that 404s
    // without ever having confirmed live is a stale leftover from a prior
    // session, not something that failed just now — surfacing it as a fresh
    // "processing failed" is a lie. A job that had been polling successfully
    // and only then 404s (e.g. genuinely deleted mid-flight) is a real failure
    // and keeps the existing failed-status treatment either way.
    const everGotData = useRef(false);
    if (data) everGotData.current = true;
    useEffect(()=>{
        if (job.status !== 'processing' || completing.current) return;
        if (error && isNotFound(error)) {
            if (isRehydratedJob(job.batchId) && !everGotData.current) {
                removeJob(job.batchId);
                return;
            }
            patchJob(job.batchId, {
                status: 'failed',
                error: 'Processing batch no longer exists'
            });
            return;
        }
        if (!data) return;
        if (data.status === 'failed' || data.projectAnalysisStatus === 'failed') {
            patchJob(job.batchId, {
                status: 'failed',
                error: 'Document processing failed'
            });
            toast.error(`${job.label} failed.`);
            return;
        }
        const awardPrepared = job.kind === 'award-intake' && data.projectAnalysisStatus === 'not_required' && data.totalDocuments > 0 && data.processedDocuments === data.totalDocuments && data.failedDocuments === 0;
        if (job.kind === 'award-intake' && data.status === 'completed' && data.projectAnalysisStatus === 'not_required' && !awardPrepared) {
            patchJob(job.batchId, {
                status: 'failed',
                error: 'Award document preparation incomplete'
            });
            toast.error(`${job.label} failed.`);
            return;
        }
        const done = data.status === 'completed' && (data.projectAnalysisStatus === 'completed' || awardPrepared);
        if (!done) {
            // Age timeout only after a fresh poll confirms the batch is still
            // unfinished: a job rehydrated long after the page was away may have
            // completed server-side, and timing out before the first response
            // would show a false failure and hide the completion link.
            if (Date.now() - job.startedAt > MAX_PROCESSING_MS) {
                patchJob(job.batchId, {
                    status: 'failed',
                    error: 'Processing timed out — try again'
                });
                toast.error(`${job.label} timed out — try again.`);
                return;
            }
            const percent = blendedPercent(data);
            if (percent !== job.percent) patchJob(job.batchId, {
                percent
            });
            return;
        }
        // The upload was refused as not a signed contract: nothing was synced,
        // so there is no program to link — block on the tray alert instead.
        if (job.kind === 'contract-docs' && data.notAContract) {
            completing.current = true;
            const reason = data.notAContract;
            const copy = notAContractCopy(reason);
            toast.error(`${copy.title}. ${copy.body}`);
            void (async ()=>{
                // The upload control for a contract lives on its program's Docs >
                // Modifications tab; fall back to the project page.
                const programLink = tenantId ? await resolveProgramLink(tenantId, job.zouProjectId, data.syncedProgramId) : null;
                patchJob(job.batchId, {
                    status: 'failed',
                    error: copy.title,
                    notAContract: reason,
                    link: programLink ? `${programLink}/docs?tab=modifications` : job.link
                });
            })();
            return;
        }
        completing.current = true;
        void (async ()=>{
            try {
                // Fan out invalidation so every surface that should now show the
                // processed data refetches: programs list/detail, command center
                // rollups, the pre-award workspace, and the zou project itself.
                await Promise.all([
                    queryClient.invalidateQueries({
                        queryKey: [
                            'program'
                        ]
                    }),
                    queryClient.invalidateQueries({
                        queryKey: [
                            'command-center'
                        ]
                    }),
                    queryClient.invalidateQueries({
                        queryKey: [
                            'project',
                            job.zouProjectId
                        ]
                    }),
                    queryClient.invalidateQueries({
                        queryKey: [
                            'projectFiles',
                            job.zouProjectId
                        ]
                    }),
                    job.meta?.preAwardProjectId ? queryClient.invalidateQueries({
                        queryKey: [
                            'pre-award',
                            job.meta.preAwardProjectId
                        ]
                    }) : Promise.resolve()
                ]);
            } catch  {
                // invalidateQueries should never throw, but if it does we must not
                // leave the job frozen at status=processing with completing.current
                // permanently locked. Reset so the next poll can retry.
                completing.current = false;
                return;
            }
            // Contract-doc batches sync a delivery program server-side (reverie →
            // ryugu); resolve it by zou project id so View lands on the program.
            // Falls back to the link registered at job start (the project
            // workspace) when the program isn't visible after the retries.
            let link = job.link;
            if (job.kind === 'contract-docs' && tenantId) {
                link = await resolveProgramLink(tenantId, job.zouProjectId, data.syncedProgramId) ?? job.link;
                if (link === job.link) {
                    // Programs page invalidation already fired above; once the
                    // projection catches up the list shows it without a refresh.
                    queryClient.invalidateQueries({
                        queryKey: [
                            'program'
                        ]
                    });
                }
            }
            patchJob(job.batchId, {
                status: 'ready',
                percent: 100,
                link
            });
            // Emit an honest completion signal: warn when any document failed
            // rather than celebrating a batch that only partially succeeded.
            // failedDocuments is the authoritative batch-scoped count from the proto.
            // The documents array comes from a separate listProjectFiles call which
            // is project-wide (not batch-scoped) — a file that failed in a previous
            // upload would flip this to true for an otherwise clean batch.
            const hasDocumentFailures = data.failedDocuments > 0;
            // program_sync_failed is only ever set on the contract pipeline; gate on
            // the job kind too so a future backend change can't mislabel a proposal /
            // award-intake batch as a contract retry.
            const syncFailed = data.programSyncFailed === true && job.kind === 'contract-docs';
            // no_contract_award is the non-retryable sibling of program_sync_failed:
            // the documents stored but no award was analyzed, so nothing landed in
            // Delivery. Same job-kind gate.
            const noAward = data.noContractAward === true && job.kind === 'contract-docs';
            // program_docs_incomplete: the program WAS created, but some source docs
            // didn't land in its Docs tab. Retryable (the retry finishes the links),
            // with distinct copy so we don't send the user hunting for a program that
            // already exists.
            const docsIncomplete = data.programDocsIncomplete === true && job.kind === 'contract-docs';
            const viewAction = link ? {
                label: 'View',
                onClick: ()=>navigate(link)
            } : undefined;
            // The retryable contract branches recover THIS batch's own documents.
            // Batch-scoped attachment ids, not the project-wide list, so the retry
            // doesn't touch unrelated/in-flight files. The `submit` differs by branch:
            // a missing program needs a full re-drive (processBatch), a
            // program-that-exists-but-some-docs-unlinked needs only the proportional
            // re-link (relinkProgramDocuments) — no re-extraction.
            const offerContractRetry = (message, submit = proposalProjectApiConnect.processBatch)=>{
                const retryIds = data.attachmentIds ?? [];
                const toastId = toast.warning(message, {
                    action: retryIds.length > 0 ? {
                        label: 'Retry',
                        onClick: ()=>{
                            // Dismiss immediately so a second click before the toast
                            // auto-closes can't spawn a duplicate batch.
                            toast.dismiss(toastId);
                            void (async ()=>{
                                try {
                                    const resp = await submit(job.zouProjectId, retryIds);
                                    useProcessingJobsStore.getState().registerJob({
                                        batchId: resp.batchId,
                                        zouProjectId: job.zouProjectId,
                                        kind: 'contract-docs',
                                        label: job.label,
                                        link: job.link
                                    });
                                } catch  {
                                    toast.error('Could not start the retry — try again.');
                                }
                            })();
                        }
                    } : viewAction
                });
            };
            if (syncFailed) {
                // The documents extracted, but reverie never created/linked the delivery
                // program — warn and offer a re-drive so the user isn't left with an
                // invisible contract.
                offerContractRetry(`${job.label}: documents processed, but the delivery program wasn't created. Retry to finish linking it.`);
            } else if (docsIncomplete) {
                // The program exists — only some Docs-tab rows are missing. Re-link just
                // those documents (no re-extraction) rather than re-driving the batch.
                offerContractRetry(`${job.label}: the delivery program was created, but some documents aren't linked yet. Retry to finish linking them.`, proposalProjectApiConnect.relinkProgramDocuments);
            } else if (noAward) {
                // The documents stored, but no contract award was analyzed (e.g. only a
                // PWS/SOW was uploaded), so nothing landed in Delivery. Not retryable —
                // re-running the same batch can't conjure an award — so warn without a
                // Retry action.
                toast.warning(`${job.label}: documents processed, but no contract award was found, so no delivery program was created.`, {
                    action: viewAction
                });
            } else if (hasDocumentFailures) {
                toast.warning(`${job.label} finished with errors — some documents may need re-processing.`, {
                    action: viewAction
                });
            } else {
                toast.success(`${job.label} complete`, {
                    action: viewAction
                });
            }
        })();
    }, [
        data,
        error,
        job,
        patchJob,
        removeJob,
        queryClient,
        navigate,
        tenantId
    ]);
    return null;
}
